import {consolidationPrepareSteps} from '../constants';
import {sortProfilesByName} from 'feedback-system/utils/sortProfilesByName';
import {USER_ROLE} from 'feedback-system/modules/user-profile/constants';
import {TeamLeaders} from '../typedef';
import {Profile} from 'feedback-system/dto/IProfile';
import {Feedback} from 'feedback-system/dto/IFeedback';

interface GetConsolidatedFeedback {
    users: Profile[];
    deletedUsers: Profile[];
    submittedFeedback: Feedback[];
    consolidatedFeedback: {[key: number]: Feedback};
    usersWithTeamLeaders: {[userId: string]: TeamLeaders};
}

const isTLRole = (primaryPosition: string) => {
    return (
        primaryPosition === USER_ROLE.TL ||
        primaryPosition === USER_ROLE.ASSOCIATED_TL ||
        primaryPosition === USER_ROLE.BOARD
    );
};

export const getConsolidatedFeedback = ({
    users,
    deletedUsers,
    submittedFeedback,
    consolidatedFeedback,
    usersWithTeamLeaders
}: GetConsolidatedFeedback) => {
    const allUsers = [...users, ...deletedUsers];
    return users
        .sort((prevUser: Profile, nextUser: Profile) => {
            return sortProfilesByName(prevUser, nextUser);
        })
        .map((user) => {
            let consolidationPrepareStep = consolidationPrepareSteps.NO_FEEDBACK;

            const feedbackConsolidated = consolidatedFeedback[user.id];

            const feedback = submittedFeedback
                .filter((fb) => fb.recipientId === user.id && fb.feedbackType !== 'CONSOLIDATED')
                .map((fb) => ({...fb, author: allUsers.find((us) => us.id === fb.authorId)}))
                .sort((prevFb, nextFb) => {
                    if (prevFb.author && nextFb.author) {
                        return sortProfilesByName(prevFb.author, nextFb.author);
                    }
                    return 0;
                })
                .sort((prevFb, nextFb) => {
                    const prevAuthor = prevFb.author;
                    const nextAuthor = nextFb.author;
                    const prevAuthorRole =
                        prevAuthor && prevAuthor.primaryPosition && isTLRole(prevAuthor.primaryPosition);
                    const nextAuthorRole =
                        nextAuthor && nextAuthor.primaryPosition && isTLRole(nextAuthor.primaryPosition);
                    if (!prevAuthorRole) {
                        return 1;
                    }
                    if (!nextAuthorRole) {
                        return -1;
                    }
                    if (!prevAuthorRole || prevAuthorRole < nextAuthorRole) {
                        return 1;
                    }
                    if (!prevAuthorRole > nextAuthorRole) {
                        return -1;
                    }
                    return 0;
                });

            if (feedback.length) {
                consolidationPrepareStep = consolidationPrepareSteps.ONLY_USER_FEEDBACK;
            }

            const teamLeaders = usersWithTeamLeaders[user.id] ? usersWithTeamLeaders[user.id] : null;

            const hasPMRating = (author: Profile | void) =>
                !!(author && author.primaryPosition && author.primaryPosition === USER_ROLE.PM);

            const hasTLRating = (author: Profile | void) =>
                !!(author && author.primaryPosition && isTLRole(author.primaryPosition));

            const isOnlyPM =
                feedback.some((fb) => hasPMRating(fb.author)) && feedback.some((fb) => !hasTLRating(fb.author));

            if (isOnlyPM) {
                consolidationPrepareStep = consolidationPrepareSteps.ONLY_PM_RATING;
            }

            const isOnlyTL = feedback.some(
                (fb) => teamLeaders?.mainTeamLead === fb.authorId || teamLeaders?.teamLeadIds.includes(fb.authorId)
            );

            if (isOnlyTL) {
                consolidationPrepareStep = consolidationPrepareSteps.ONLY_TL_RATING;
            }

            if (feedbackConsolidated && feedbackConsolidated.feedbackData && feedbackConsolidated.feedbackData.RATING) {
                consolidationPrepareStep = consolidationPrepareSteps.MANAGER_RATING;
            }

            const isUpdated = feedback.some((fb) => fb.isUpdated);
            if (isUpdated) {
                consolidationPrepareStep = consolidationPrepareSteps.UPDATED_FEEDBACK;
            }

            return {
                feedbackConsolidated: feedbackConsolidated ? feedbackConsolidated : null,
                profile: user,
                feedbacks: feedback,
                consolidationPrepareStep,
                teamLeaders
            };
        });
};
